import { getCategoryProfile } from "./category-profiles";
import { applyFormConfigToSteps, createEffectiveSchemaHash, createFormCatalogFromSteps, DEFAULT_FORM_CONFIG, type EffectiveIntakeSchema, type FormConfigVersion, type FormRuleContext } from "./form-config";
import type { IntakeData, IntakeStep } from "./types";

export {
  applyCategoryPreset,
  createDefaultConsultationProduct,
  createDefaultIntakeData,
  createDoctorProfile,
  createDraftId,
  createProcedureDetail,
  createProductOption,
  DEPOSIT_CONTRACT_TYPES,
  FIXED_UPSELL_COMMISSION_RATE,
  isDepositContractType,
  isUpsellContractType,
  LOCAL_STORAGE_KEY,
  normalizeContractOfferForCategory,
  normalizeContractTypeForCategory,
  parseOfferedContractTypes,
  resolveConsultationProductName,
  sanitizeUpsellRate,
  UPSELL_CONTRACT_TYPES,
} from "./form-schema/default-data";

import { FIXED_UPSELL_COMMISSION_RATE, parseOfferedContractTypes, sanitizeUpsellRate } from "./form-schema/default-data";

import { buildProfileAdjustedIntakeStepsForData } from "./form-schema/profile-adjustments";
export { DOCUMENT_UPLOADS, MAX_LISTING_PHOTOS, PHOTO_UPLOADS } from "./form-schema/uploads";

import { INTAKE_STEPS } from "./form-schema/intake-steps";

export { INTAKE_STEPS, REVIEW_BENEFIT_HELPER_TEXT } from "./form-schema/intake-steps";

export function buildProfileAdjustedIntakeSteps(data: IntakeData, config: FormConfigVersion = DEFAULT_FORM_CONFIG): IntakeStep[] {
  return buildProfileAdjustedIntakeStepsForData(data, INTAKE_STEPS, config);
}

export function getEffectiveIntakeSchema(data: IntakeData, config: FormConfigVersion = DEFAULT_FORM_CONFIG): EffectiveIntakeSchema {
  const profile = getCategoryProfile(data.category?.categoryId);
  const baseSteps = buildProfileAdjustedIntakeSteps(data, config);
  const context: FormRuleContext = profile
    ? { categoryId: profile.id, domain: profile.domain, contractType: data.contract?.contractType }
    : { categoryId: "", domain: "", contractType: data.contract?.contractType };
  const schema = applyFormConfigToSteps(baseSteps, context, config, data);
  const policyAdjustedSteps = applyConditionalPolicyFields(schema.steps, data);
  const steps = applyContractOfferToSteps(policyAdjustedSteps, data);
  return {
    ...schema,
    steps,
    catalog: createFormCatalogFromSteps(steps),
    schemaHash: createEffectiveSchemaHash(steps, schema.configVersionId, context, schema.appliedRules),
  };
}

function applyConditionalPolicyFields(steps: IntakeStep[], data: IntakeData): IntakeStep[] {
  const chargesConsultationFee = data.policy.consultationFeePolicy === "charged";
  return steps.map((step) => {
    if (step.id !== "policy") return step;
    const fields = step.fields
      ?.filter((field) => field.id !== "consultationFeeAmount" || chargesConsultationFee)
      .map((field) => field.id === "consultationFeeAmount" ? { ...field, required: true } : field);
    return { ...step, fields };
  });
}

// 계약 단계에 초대 오퍼(옵션 제공 범위)와 현재 수수료율을 반영한다.
// 옵션 라벨의 "업셀링 n%"는 항상 실제 수수료율로 치환하며, 오퍼 수수료율은 기본값 역할만 한다(파트너 수정 가능).
function applyContractOfferToSteps(steps: IntakeStep[], data: IntakeData): IntakeStep[] {
  const offeredTypes = parseOfferedContractTypes(data.contract?.offeredContractTypes);
  const offeredRate = sanitizeUpsellRate(data.contract?.offeredUpsellCommissionRate);
  const rate = sanitizeUpsellRate(data.contract?.upsellCommissionRate) || offeredRate || FIXED_UPSELL_COMMISSION_RATE;
  const hasCustomRate = rate !== FIXED_UPSELL_COMMISSION_RATE;

  const applyRateText = (text: string) => {
    const withOptionRate = text.replace(/업셀링 (?:n|\d+(?:\.\d+)?)%/g, `업셀링 ${rate}%`);
    return hasCustomRate ? withOptionRate.split(`수수료 ${FIXED_UPSELL_COMMISSION_RATE}%`).join(`수수료 ${rate}%`) : withOptionRate;
  };

  return steps.map((step) => {
    if (step.id !== "contract") return step;
    const fields = step.fields?.map((field) => {
      if (field.id === "contractType" && field.options?.length) {
        const filtered = offeredTypes.length ? field.options.filter((option) => offeredTypes.includes(option.value)) : field.options;
        const options = (filtered.length ? filtered : field.options).map((option) => ({ ...option, label: applyRateText(option.label) }));
        const defaultValue = options.some((option) => option.value === String(field.defaultValue ?? "")) ? field.defaultValue : options[0]?.value;
        return { ...field, options, defaultValue };
      }
      if (field.id === "upsellCommissionRate" && offeredRate) {
        return {
          ...field,
          defaultValue: offeredRate,
          helperText: `현장에서 실제로 받는 상품/서비스 금액 기준으로 업셀링 수수료를 수취합니다. 담당자가 안내한 기본값 ${offeredRate}%가 채워져 있어요. 협의된 내용과 다르면 수정해주세요. 최종 수수료율은 계약 과정에서 확정됩니다. 부가세 별도입니다.`,
        };
      }
      return field;
    });
    const tips = step.tips?.map((tip) => ({ ...tip, body: applyRateText(tip.body) }));
    const description = offeredTypes.length === 1 ? "담당자가 안내한 계약 구조입니다. 내용을 확인해주세요." : step.description;
    return { ...step, fields, tips, description };
  });
}

export function buildIntakeSteps(data: IntakeData, config: FormConfigVersion = DEFAULT_FORM_CONFIG): IntakeStep[] {
  return getEffectiveIntakeSchema(data, config).steps;
}
